import React from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Check, X, RefreshCw, Waypoints } from 'lucide-react';
import { EFFECT_TYPE_LABELS_FI, EFFECT_TYPE_COLORS } from '../../config/effectTypes';
import { valenceStyle } from '../../config/valence';

/**
 * What the AI thinks it found, waiting for someone to agree.
 *
 * Nothing here is on the map yet. A loop and a cross-branch link are the two
 * strongest claims the map can carry — one says a chain compounds on itself,
 * the other that a consequence has a cause somewhere else entirely — so they
 * are the last things that should appear without a person saying yes
 * (specs/impact-path-v2-prd.md §2).
 *
 * Each proposal shows the relation, how it would act, and why: a suggestion you
 * cannot argue with is one you can only rubber-stamp.
 */

const rise = keyframes`
  from { opacity: 0; transform: translate(-50%, 12px); }
  to   { opacity: 1; transform: translate(-50%, 0); }
`;

const Panel = styled.div`
  position: absolute;
  left: 50%;
  bottom: 92px;
  transform: translateX(-50%);
  z-index: 55;
  width: min(720px, calc(100vw - 40px));
  max-height: 46vh;
  overflow-y: auto;
  padding: 14px;
  border-radius: 16px;
  background: rgba(20, 20, 31, 0.97);
  backdrop-filter: blur(10px);
  border: 1px solid ${(p) => p.$color}66;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.6);
  animation: ${rise} 0.2s cubic-bezier(0.2, 0.9, 0.3, 1);
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
  margin-bottom: 10px;
  font-size: 0.9rem;
  font-weight: 700;
  color: ${(p) => p.$color};

  svg { width: 16px; height: 16px; }
  .spacer { flex: 1; }

  button {
    background: none;
    border: none;
    color: rgba(255, 255, 255, 0.45);
    cursor: pointer;
    font-size: 1.1rem;
    &:hover { color: #fff; }
  }
`;

const Empty = styled.div`
  padding: 6px 2px 2px;
  font-size: 0.85rem;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.5);
`;

/**
 * The ring itself, not a sentence about it.
 *
 * "A vahvistaa B" is true of a great many pairs; what makes it a loop is the
 * chain in between, and that was the one thing the proposal did not show. The
 * chain runs left to right the way it does on the map, and the return arc under
 * it carries the type — which is also where the type is changed, because the
 * closing link is what decides whether the ring reinforces or balances.
 */
const Chain = styled.div`
  position: relative;
  padding-bottom: 26px;
  margin-top: 3px;

  .links {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 5px;
  }

  .chip {
    padding: 4px 9px;
    border-radius: 7px;
    background: rgba(255, 255, 255, 0.07);
    border: 1px solid rgba(255, 255, 255, 0.12);
    font-size: 0.79rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
  }

  .chip.end { border-color: ${(p) => p.$color}; color: #fff; }
  .chip.gap { background: none; border: none; opacity: 0.4; padding: 4px 2px; }

  .arrow { color: rgba(255, 255, 255, 0.3); font-size: 0.8rem; }

  /* Comes back to where it started: the arc spans the whole chain and the
     arrowhead lands on the node the ring closes onto. */
  .return {
    position: absolute;
    left: 6px;
    right: 6px;
    bottom: 6px;
    height: 13px;
    border: 2px solid ${(p) => p.$color};
    border-top: none;
    border-radius: 0 0 9px 9px;
  }

  .head {
    position: absolute;
    left: 1px;
    bottom: 15px;
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-bottom: 7px solid ${(p) => p.$color};
  }
`;

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 11px;
  padding: 11px;
  border-radius: 11px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);

  & + & { margin-top: 7px; }

  .body { flex: 1; min-width: 0; }

  .relation {
    font-size: 0.9rem;
    font-weight: 600;
    color: #fff;
    line-height: 1.4;
  }

  .type {
    display: inline-block;
    margin: 0 6px;
    padding: 2px 9px;
    border-radius: 999px;
    border: 1px solid transparent;
    font-family: inherit;
    font-size: 0.76rem;
    font-weight: 700;
    cursor: pointer;

    &:hover { border-color: currentColor; }
  }

  .why {
    margin-top: 5px;
    font-size: 0.79rem;
    line-height: 1.45;
    color: rgba(255, 255, 255, 0.5);
  }

  .actions { display: flex; gap: 6px; flex-shrink: 0; }

  .actions button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 8px;
    cursor: pointer;
    border: 1px solid rgba(255, 255, 255, 0.14);
    background: rgba(255, 255, 255, 0.05);
    color: rgba(255, 255, 255, 0.6);

    svg { width: 15px; height: 15px; }
    &:hover { color: #fff; }
  }

  .actions .accept:hover { background: rgba(76, 175, 80, 0.25); border-color: #4CAF50; }
  .actions .reject:hover { background: rgba(239, 83, 80, 0.2); border-color: #EF5350; }
`;

const PillButton = styled.button`
  padding: 2px 9px;
  border-radius: 999px;
  border: 1px solid transparent;
  font-family: inherit;
  font-size: 0.76rem;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;

  &:hover { border-color: currentColor; }

  ${(p) => p.$floating && `
    position: absolute;
    left: 50%;
    bottom: -1px;
    transform: translateX(-50%);
    background-color: #14141f;
  `}
`;

// A node named in a proposal wears its valence, as it does on the map.
const End = styled.span`
  ${(p) => p.$valence && `
    padding: 1px 6px; border-radius: 6px;
    border: 1px solid ${valenceStyle(p.$valence).color};
    background: ${valenceStyle(p.$valence).tint};
  `}
`;

function TypePill({ s, onTypeClick, floating = false }) {
  const color = EFFECT_TYPE_COLORS[s.effectType];
  return (
    <PillButton
      type="button"
      $floating={floating}
      style={{ color, background: floating ? '#14141f' : `${color}22`, borderColor: floating ? color : 'transparent' }}
      title="Vaihda suhteen tyyppi"
      onClick={(e) => {
        const box = e.currentTarget.getBoundingClientRect();
        onTypeClick?.(s, { x: box.left + box.width / 2, y: box.top + box.height / 2 });
      }}
    >
      {EFFECT_TYPE_LABELS_FI[s.effectType] || s.effectType}
    </PillButton>
  );
}

export default function SuggestionsPanel({
  kind, suggestions = [], error, onAccept, onDismiss, onClose, onTypeClick, chainOf,
}) {
  const isLoops = kind === 'loops';
  const color = isLoops ? '#D4A72C' : '#96BEF5';
  const Icon = isLoops ? RefreshCw : Waypoints;

  return (
    <Panel $color={color}>
      <Head $color={color}>
        <Icon />
        {isLoops ? 'Ehdotetut silmukat' : 'Ehdotetut ristiinyhteydet'}
        <span className="spacer" />
        <button type="button" onClick={onClose} aria-label="Sulje">×</button>
      </Head>

      {error && <Empty>{error}</Empty>}

      {!error && suggestions.length === 0 && (
        <Empty>
          {isLoops
            ? 'AI ei löytänyt ketjuja, jotka palaisivat itseensä. Se ei tarkoita ettei niitä ole — piirrä silmukka käsin, jos näet sellaisen.'
            : 'AI ei löytänyt haarojen välisiä yhteyksiä, joita kartalla ei jo ole.'}
        </Empty>
      )}

      {suggestions.map((s) => (
        <Row key={`${s.fromId}->${s.toId}`}>
          <div className="body">
            {isLoops && chainOf?.(s)?.length > 1 ? (
              <Chain $color={EFFECT_TYPE_COLORS[s.effectType]}>
                <div className="links">
                  {chainOf(s).map((step, i) => (
                    <React.Fragment key={`${step.id || step.text}-${i}`}>
                      {i > 0 && <span className="arrow">→</span>}
                      {/* Each step wears its node's valence, as on the map: the
                          pill says how the loop turns, the chips say what it
                          turns towards. A green pill on a chain of red chips
                          is a vicious circle, and now looks like one. */}
                      <span
                        className={`chip${step.gap ? ' gap' : ''}${i === 0 ? ' end' : ''}`}
                        style={step.valence ? { borderColor: valenceStyle(step.valence).color, background: valenceStyle(step.valence).tint } : undefined}
                      >
                        {step.gap ? '…' : step.text}
                      </span>
                    </React.Fragment>
                  ))}
                </div>
                <span className="head" />
                <span className="return" />
                <TypePill s={s} onTypeClick={onTypeClick} floating />
              </Chain>
            ) : (
            <div className="relation">
              <End $valence={s.fromValence}>{s.fromText}</End>
              {/* The AI's reading of how the relation acts, pre-picked but not
                  settled: it is the one part of a proposal most worth arguing
                  with, and on a map built in parallel the honest answer is
                  often "sama asia" rather than any causal type at all. */}
              {' '}<TypePill s={s} onTypeClick={onTypeClick} />{' '}
              <End $valence={s.toValence}>{s.toText}</End>
            </div>
            )}
            {s.reasoning && <div className="why">{s.reasoning}</div>}
          </div>
          <div className="actions">
            <button type="button" className="accept" onClick={() => onAccept?.(s)} title="Lisää kartalle">
              <Check />
            </button>
            <button type="button" className="reject" onClick={() => onDismiss?.(s)} title="Hylkää">
              <X />
            </button>
          </div>
        </Row>
      ))}
    </Panel>
  );
}
